import { getState, findFooter } from './storage.js';
import { isTruthy } from '../../shared/is-truthy.js';
import { createGridRenderer, createRowQuery } from './grid-render.js';
import { bindGridEvents } from './bind-grid-events.js';
import { attachToGrid } from '../../shared/truncated-text-hover-scroll.js';
import { setSlidingFilterValue, syncSlidingFilterWrappers } from '../../components/sliding-filter.js';

function applySlidingFilterGroup(wrapperIds, value) {
    if (!wrapperIds.length) {
        return;
    }
    const primaryId = wrapperIds[0];
    const wrapper = document.getElementById(primaryId);
    if (!wrapper) {
        return;
    }
    const normalized = value == null ? '' : String(value);
    setSlidingFilterValue(wrapper, normalized, { animate: false });
    syncSlidingFilterWrappers(wrapperIds, normalized, primaryId);
}

const FILTER_RENDER_DEFER_MS = 200;

function lifetimeKeyFor(storageKeyPrefix) {
    return '_dataGridLifetime_' + storageKeyPrefix;
}

export function abortDataGridLifetime(storageKeyPrefix) {
    const key = lifetimeKeyFor(storageKeyPrefix);
    const lifetime = window[key];
    if (!lifetime) {
        return;
    }
    lifetime.abort();
    window[key] = null;
}

export function initDataGridInstance(grid, options) {
    if (!grid || !options) return;
    const guardAttr = options.initGuardAttr || 'data-data-grid-init';
    if (isTruthy(grid.getAttribute(guardAttr))) return;
    grid.setAttribute(guardAttr, '1');

    const lifetimeKey = lifetimeKeyFor(options.storageKeyPrefix);
    abortDataGridLifetime(options.storageKeyPrefix);
    const lifetime = window[lifetimeKey] = new AbortController();
    const listen = { signal: lifetime.signal };

    const storageKey = options.storageKeyPrefix + '_state';
    const searchStorageKey = options.searchStorageKey || options.storageKeyPrefix + '_search';
    const statusStorageKey = options.statusStorageKey || options.storageKeyPrefix + '_status';
    const recurringStatusStorageKey = options.recurringStatusStorageKey || options.storageKeyPrefix + '_recurring_status';
    const typeStorageKey = options.typeStorageKey || options.storageKeyPrefix + '_type';
    const body = grid.querySelector('.data-grid-body');
    const footer = findFooter(grid);
    if (!body) {
        grid.removeAttribute(guardAttr);
        return;
    }

    const sourceOrderByItem = new WeakMap();
    let nextSourceOrder = 0;

    function getItems() {
        const items = Array.from(body.querySelectorAll('.data-grid-item'));
        items.forEach((item) => {
            if (!sourceOrderByItem.has(item)) sourceOrderByItem.set(item, nextSourceOrder++);
        });
        return items.sort((a, b) => sourceOrderByItem.get(a) - sourceOrderByItem.get(b));
    }

    const infoEl = footer?.querySelector('.data-grid-info') || null;
    const lengthEl = footer?.querySelector('.data-grid-length select') || null;
    const paginationEl = footer?.querySelector('.data-grid-pagination') || null;

    const createSearchMatcher = options.createSearchMatcher || (() => () => true);
    const rowMatchesStatus = options.rowMatchesStatus || (() => true);
    const rowMatchesType = options.rowMatchesType || (() => true);
    const rowMatchesDateRangeFn = options.rowMatchesDateRange;
    const dateFromSelector = options.dateFromSelector || '';
    const dateToSelector = options.dateToSelector || '';
    const persistDateRange = options.persistDateRange === true;
    const dateRangeEnabled = typeof rowMatchesDateRangeFn === 'function' && dateFromSelector && dateToSelector;
    const afterDateRangeChange = options.afterDateRangeChange;
    const getSortValue = options.getSortValue || (() => '');
    const emptyMessage = options.emptyMessage || 'No rows';
    const checkboxClass = options.checkboxClass || 'data-grid-row-checkbox';
    const selectAllId = options.selectAllId || 'data-grid-select-all';
    const defaultSortKey = options.defaultSortKey || '';
    const defaultSortDir = options.defaultSortDir || 'asc';
    const defaultPageLength = options.defaultPageLength ?? 50;
    const mobilePageLength = options.mobilePageLength ?? 10;
    const searchSelector = options.searchSelector || '';
    const statusFilterIds = options.statusFilterIds || [];
    const recurringStatusFilterIds = options.recurringStatusFilterIds || [];
    const typeFilterIds = options.typeFilterIds || [];
    const createCampaignMatcher = options.createCampaignMatcher;

    const ignorePersistedFilters = options.ignorePersistedFilters === true;
    const persistedState = ignorePersistedFilters ? {} : getState(storageKey);
    const mobileMq = window.matchMedia('(max-width: 768px)');
    const isMobile = mobileMq.matches;
    const state = {
        searchQuery: ignorePersistedFilters ? '' : (persistedState.search || ''),
        statusFilter: ignorePersistedFilters ? '' : (persistedState.statusFilter || ''),
        recurringStatusFilter: ignorePersistedFilters ? '' : (persistedState.recurringStatusFilter || ''),
        typeFilter: ignorePersistedFilters ? '' : (persistedState.typeFilter || options.defaultTypeFilter || ''),
        sortKey: persistedState.sortKey || defaultSortKey,
        sortDir: persistedState.sortDir || defaultSortDir,
        page: Math.max(1, parseInt(persistedState.page, 10) || 1),
        pageLength: parseInt(persistedState.pageLength, 10) || (isMobile ? mobilePageLength : defaultPageLength),
        dateFrom: '',
        dateTo: '',
    };

    if (dateRangeEnabled && persistDateRange) {
        state.dateFrom = persistedState.dateFrom || '';
        state.dateTo = persistedState.dateTo || '';
    } else if (dateRangeEnabled) {
        const firstFrom = document.querySelector(dateFromSelector);
        const firstTo = document.querySelector(dateToSelector);
        state.dateFrom = firstFrom ? firstFrom.value : '';
        state.dateTo = firstTo ? firstTo.value : '';
    }

    function setItemVisible(item, visible) {
        item.classList.toggle('data-grid-item-hidden', !visible);
        item.classList.toggle('data-grid-item-visible', visible);
    }

    const { getFilteredRows, getSortedRows } = createRowQuery({
        getItems,
        getSortValue,
        createSearchMatcher,
        rowMatchesStatus,
        rowMatchesType,
        createCampaignMatcher,
        rowMatchesDateRangeFn,
        dateRangeEnabled,
    });

    const renderer = createGridRenderer({
        grid,
        body,
        storageKey,
        infoEl,
        paginationEl,
        emptyMessage,
        dateRangeEnabled,
        persistDateRange,
        getItems,
        getFilteredRows,
        getSortedRows,
        setItemVisible,
        materializeItem: materializeMobileContent,
        searchSelector,
    });

    lifetime.signal.addEventListener('abort', () => renderer.cancelPending());

    function render(renderOptions) {
        renderer.render(state, renderOptions);
    }

    function syncSearchInputs(value) {
        renderer.syncSearchInputs(value);
    }

    function syncDateRangeInputs() {
        renderer.syncDateRangeInputs(state, dateFromSelector, dateToSelector);
    }

    function notifyDateRangeChange() {
        if (typeof afterDateRangeChange !== 'function') return;
        afterDateRangeChange();
    }

    function syncSearchQueryFromStorage() {
        try {
            const saved = sessionStorage.getItem(searchStorageKey);
            if (saved != null) {
                state.searchQuery = saved;
                syncSearchInputs(state.searchQuery);
                return;
            }
        } catch (error) {
            console.error(error);
        }
        state.searchQuery = '';
        syncSearchInputs('');
    }

    function getItemCheckboxes(item) {
        const checkboxName = options.checkboxName;
        if (!checkboxName || !item) {
            return [];
        }
        return Array.from(item.querySelectorAll(`input[type="checkbox"][name="${checkboxName}"]`));
    }

    function materializeMobileContent(item) {
        if (!mobileMq.matches) return;
        const template = item.querySelector(':scope > template[data-grid-mobile-deferred]');
        if (!template) return;
        const checked = getItemCheckboxes(item).some((cb) => cb.checked);
        template.replaceWith(template.content);
        getItemCheckboxes(item).forEach((cb) => { cb.checked = checked; });
    }

    function countCheckedItems() {
        let count = 0;
        body.querySelectorAll('.data-grid-item-visible').forEach((item) => {
            if (getItemCheckboxes(item).some((cb) => cb.checked)) {
                count++;
            }
        });
        return count;
    }

    function emitSelectionChange() {
        grid.dispatchEvent(new CustomEvent('data-grid-selection-change', {
            bubbles: true,
            detail: {
                gridId: grid.id,
                checkedCount: countCheckedItems(),
            },
        }));
    }

    function onBreakpointChange() {
        if (!grid.isConnected) return;
        state.pageLength = mobileMq.matches ? mobilePageLength : defaultPageLength;
        if (lengthEl) lengthEl.value = String(state.pageLength);
        state.page = 1;
        render();
    }
    mobileMq.addEventListener('change', onBreakpointChange, listen);

    bindGridEvents({
        grid,
        body,
        options,
        listen,
        lifetime,
        state,
        api: {
            render,
            getItemCheckboxes,
            emitSelectionChange,
            syncSearchQueryFromStorage,
            syncDateRangeInputs,
            notifyDateRangeChange,
        },
        statusFilterIds,
        recurringStatusFilterIds,
        typeFilterIds,
        statusStorageKey,
        recurringStatusStorageKey,
        typeStorageKey,
        searchStorageKey,
        dateRangeEnabled,
        dateFromSelector,
        dateToSelector,
        searchSelector,
        lengthEl,
        defaultPageLength,
        selectAllId,
        checkboxClass,
        filterRenderDeferMs: FILTER_RENDER_DEFER_MS,
    });

    const savedStatus = ignorePersistedFilters ? null : sessionStorage.getItem(statusStorageKey);
    if (savedStatus !== null) state.statusFilter = savedStatus;
    const savedRecurringStatus = ignorePersistedFilters ? null : sessionStorage.getItem(recurringStatusStorageKey);
    if (savedRecurringStatus !== null) state.recurringStatusFilter = savedRecurringStatus;
    const savedType = ignorePersistedFilters ? null : sessionStorage.getItem(typeStorageKey);
    if (savedType !== null) {
        state.typeFilter = savedType;
    } else if (!ignorePersistedFilters && options.defaultTypeFilter != null) {
        state.typeFilter = String(options.defaultTypeFilter);
        try {
            sessionStorage.setItem(typeStorageKey, state.typeFilter);
        } catch (error) {
            console.error(error);
        }
    }
    applySlidingFilterGroup(typeFilterIds, state.typeFilter);
    applySlidingFilterGroup(statusFilterIds, state.statusFilter);
    applySlidingFilterGroup(recurringStatusFilterIds, state.recurringStatusFilter);

    const savedSearch = ignorePersistedFilters ? null : sessionStorage.getItem(searchStorageKey);
    if (savedSearch != null) {
        state.searchQuery = savedSearch;
    } else {
        state.searchQuery = '';
    }
    syncSearchInputs(state.searchQuery);

    syncDateRangeInputs();
    render({ syncBody: true });
    attachToGrid(grid);
    notifyDateRangeChange();
    grid.style.visibility = 'visible';
    requestAnimationFrame(() => {
        if (!grid.isConnected) {
            return;
        }
        render();
    });

    grid.showItemByRowId = function (targetRowId) {
        const id = String(targetRowId || '');
        if (!id) {
            return false;
        }
        const filtered = getFilteredRows(state);
        const sorted = getSortedRows(filtered, state);
        const index = sorted.findIndex((item) => item.getAttribute('data-row-id') === id);
        if (index < 0) {
            return false;
        }
        state.page = Math.floor(index / state.pageLength) + 1;
        render({ syncBody: true });
        return true;
    };

    grid.getFilteredSortedItems = function () {
        return getSortedRows(getFilteredRows(state), state);
    };
}
